// src/pages/container_rental/ContainerRentalCreatePage.tsx
//
// Single-page form (no wizard) handling both /create and /edit/:id.
// All identity / cover / content / rental-terms fields edit on one screen,
// with a sticky 1-회차 preview on the right at md+. Items are edited via the
// same ItemsEditor used by the estimate wizard so every item is visible and
// editable (drag/drop, add/remove, qty/unit/price).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";

import {
  ContainerRentalFormInput,
  ContainerRentalDto,
  RentalStatus,
} from "../../contracts/container_rental/types.ts";
import {
  containerRentalFormSchema,
} from "../../contracts/container_rental/schemas.ts";
import {
  EstimateDto,
  defaultCoverForm,
  defaultContentForm,
} from "../../contracts/estimates/types.ts";
import { designTokens } from "../../theme.ts";
import { useContainerRentalApi } from "../../hooks/useContainerRentalApi.ts";
import EstimatePickerDialog from "./components/EstimatePickerDialog.tsx";
import InitialMonthsField from "./components/InitialMonthsField.tsx";
import TotalRoundsField from "./components/TotalRoundsField.tsx";
import { ItemsEditor } from "../estimates/ItemEditor.tsx";

// 미리보기 카드 색 — 확정 디자인 시스템(흰 서피스 + 뉴트럴 보더).
// 구 "다크 슬랩(grey.900 + 흰 텍스트 하드코딩)"은 제거되었다. 테마의
// MuiCard 오버라이드(1px solid #E4E7EA + 미세 그림자)를 그대로 상속한다.
const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  border: "#E4E7EA",
  accent: "#E2451C",
};

/** 금액 표기 — 디자인 토큰 mono + 자릿수 정렬 */
const moneySx = {
  fontFamily: designTokens.fonts.mono,
  fontVariantNumeric: "tabular-nums",
} as const;

const todayIso = () => dayjs().format("YYYY-MM-DD");

// 폼은 날짜를 "YYYY-MM-DD"로 들고 있지만 백엔드는 time.Time(RFC3339)으로
// 바인딩한다 — 날짜만 보내면 ShouldBindJSON이 400을 낸다. 전송 직전에
// KST 자정 RFC3339로 변환한다 (서버의 KST 벽시계 관례와 동일 기준).
const toKstMidnightIso = (d: string) => `${d}T00:00:00+09:00`;

const fmtKrw = (n: number) =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) : "0";

const buildDefaultValues = (): ContainerRentalFormInput => ({
  estimateId: null,
  cover: { ...defaultCoverForm },
  content: { ...defaultContentForm, contentType: "임대" },
  clientName: "",
  siteName: "",
  siteAddress: "",
  contactPerson: "",
  contactPhone: "",
  containerModel: "",
  containerCount: 1,
  monthlyRent: 300000,
  transportFee: 400000,
  initialMonths: 3,
  totalRounds: 25,
  issueDate: todayIso(),
  rentalStartDate: todayIso(),
  status: "active" as RentalStatus,
  memo: "",
});

const dtoToForm = (dto: ContainerRentalDto): ContainerRentalFormInput => ({
  id: dto.id,
  estimateId: dto.estimateId ?? null,
  cover: dto.cover,
  content: dto.content,
  clientName: dto.clientName,
  siteName: dto.siteName,
  siteAddress: dto.siteAddress,
  contactPerson: dto.contactPerson,
  contactPhone: dto.contactPhone,
  containerModel: dto.containerModel,
  containerCount: dto.containerCount,
  monthlyRent: dto.monthlyRent,
  transportFee: dto.transportFee,
  initialMonths: dto.initialMonths,
  totalRounds: dto.totalRounds,
  issueDate: dto.issueDate ? dayjs(dto.issueDate).format("YYYY-MM-DD") : todayIso(),
  rentalStartDate: dto.rentalStartDate ? dayjs(dto.rentalStartDate).format("YYYY-MM-DD") : todayIso(),
  status: dto.status,
  memo: dto.memo,
});

export default function ContainerRentalCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const basePath = location.pathname.startsWith("/dashboard")
    ? "/dashboard/container-rental"
    : "/container-rental";

  const { getRental, createRental, updateRental } = useContainerRentalApi();

  const methods = useForm<ContainerRentalFormInput>({
    resolver: zodResolver(containerRentalFormSchema) as any,
    defaultValues: buildDefaultValues(),
    mode: "onBlur",
  });
  const { control, handleSubmit, reset, watch, setValue, formState } = methods;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: "error" | "success" } | null>(null);
  const [initialItems, setInitialItems] = useState<EstimateDto["content"]["items"] | undefined>(undefined);

  const watchedEstimateId = watch("estimateId");
  const watchedCover = watch("cover");
  const watchedClientName = watch("clientName");
  const watchedSiteName = watch("siteName");
  const watchedContactPerson = watch("contactPerson");
  const watchedContactPhone = watch("contactPhone");
  const watchedTransportFee = watch("transportFee");
  const watchedInitialMonths = watch("initialMonths");
  const watchedRentalStartDate = watch("rentalStartDate");
  // Use useWatch (not watch) for the items array — useFieldArray inside
  // ItemsEditor mutates content.items via its own internal store, and only
  // useWatch reliably re-fires on add/remove. Without this the preview
  // doesn't update when an item row is deleted or appended.
  const watchedItems = useWatch({ control, name: "content.items" as any }) as any[] | undefined;

  // Auto-sync cover.* → index columns when the column is empty.
  useEffect(() => {
    const c = watchedCover ?? {};
    if (!watchedClientName?.trim() && c.companyName?.trim()) {
      setValue("clientName", c.companyName, { shouldValidate: true });
    }
    if (!watchedSiteName?.trim() && c.siteName?.trim()) {
      setValue("siteName", c.siteName, { shouldValidate: true });
    }
    if (!watchedContactPerson?.trim() && c.recipientName?.trim()) {
      setValue("contactPerson", c.recipientName);
    }
    if (!watchedContactPhone?.trim() && c.recipientHP?.trim()) {
      setValue("contactPhone", c.recipientHP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCover?.companyName, watchedCover?.siteName, watchedCover?.recipientName, watchedCover?.recipientHP]);

  // Load existing rental when editing.
  const loadExisting = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const dto = await getRental(id);
      reset(dtoToForm(dto));
      setInitialItems(dto.content?.items ?? []);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "거래를 불러오지 못했습니다.", severity: "error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isEdit) loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  // Pick from estimate — copy cover + content; auto-fill index columns.
  const handlePickEstimate = (estimate: EstimateDto) => {
    setValue("estimateId", estimate.id, { shouldDirty: true });
    setValue("cover", estimate.cover ?? { ...defaultCoverForm }, { shouldDirty: true });
    setValue("content", estimate.content ?? { ...defaultContentForm }, { shouldDirty: true });
    setValue("clientName", estimate.cover?.companyName ?? "", { shouldDirty: true });
    setValue("siteName", estimate.cover?.siteName ?? "", { shouldDirty: true });
    setValue("contactPerson", estimate.cover?.recipientName ?? "", { shouldDirty: true });
    setValue("contactPhone", estimate.cover?.recipientHP ?? "", { shouldDirty: true });
    // ItemsEditor is told to replace its field array via initialItems.
    setInitialItems(estimate.content?.items ?? []);
    setSnack({ msg: "견적서 내용을 가져왔습니다. 필요한 부분만 수정하세요.", severity: "success" });
  };

  // Reset whole form back to defaults — fixes the previous bug where
  // identity columns and the items field array kept stale values because
  // setValue only touched a few fields and ItemsEditor's `length > 0` guard
  // ignored an empty initialItems array.
  const handleStartFromScratch = () => {
    reset(buildDefaultValues());
    // Tell ItemsEditor to stop replaying old initialItems; useFieldArray will
    // pick up the freshly-reset (empty) form value automatically.
    setInitialItems(undefined);
    setSnack({ msg: "입력 내용을 모두 초기화했습니다.", severity: "success" });
  };

  const preview = useMemo(() => {
    // Monthly rent total = sum over content.items where rowType==="아이템"
    // of (qty × unitPrice). Description / subtotal rows are ignored.
    const items = watchedItems ?? [];
    const monthlyTotal = items.reduce((acc: number, it: any) => {
      if (it?.rowType !== "아이템") return acc;
      const q = Number(it?.qty) || 0;
      const p = Number(it?.unitPrice) || 0;
      return acc + q * p;
    }, 0);
    const itemCount = items.filter((it: any) => it?.rowType === "아이템").length;
    const totalContainers = items.reduce((acc: number, it: any) => {
      if (it?.rowType !== "아이템") return acc;
      return acc + (Number(it?.qty) || 0);
    }, 0);

    const transport = Number(watchedTransportFee) || 0;
    const months = Number(watchedInitialMonths) || 0;

    const rentTotal = monthlyTotal * months;
    const supply = rentTotal + transport;
    const vat = Math.round(supply * 0.1);
    const total = supply + vat;

    let firstEnd = "";
    let regularStart = "";
    if (watchedRentalStartDate) {
      const start = dayjs(watchedRentalStartDate);
      if (start.isValid() && months > 0) {
        const end = start.add(months, "month").subtract(1, "day");
        firstEnd = end.format("YYYY.MM.DD");
        regularStart = end.add(1, "day").format("YYYY.MM.DD");
      }
    }
    return { monthlyTotal, itemCount, totalContainers, rentTotal, supply, vat, total, firstEnd, regularStart };
  }, [watchedItems, watchedTransportFee, watchedInitialMonths, watchedRentalStartDate]);

  const onSubmit = handleSubmit(
    async (values) => {
      const cover = (values.cover ?? {}) as Record<string, string | undefined>;
      const filled: ContainerRentalFormInput = {
        ...values,
        clientName: values.clientName?.trim() || cover.companyName || "",
        siteName: values.siteName?.trim() || cover.siteName || "",
        contactPerson: values.contactPerson?.trim() || cover.recipientName || "",
        contactPhone: values.contactPhone?.trim() || cover.recipientHP || "",
        // 서버 바인딩 계약: 날짜는 RFC3339, estimateId 는 UUID 또는 null.
        // hidden input 을 거친 estimateId 는 미선택 시 "" 가 되는데 *uuid.UUID
        // 언마샬이 빈 문자열에서 실패(400)하므로 null 로 정규화한다.
        issueDate: toKstMidnightIso(values.issueDate),
        rentalStartDate: toKstMidnightIso(values.rentalStartDate),
        estimateId: values.estimateId || null,
      };
      setSubmitting(true);
      try {
        if (isEdit && id) {
          const updated = await updateRental({ ...filled, id });
          setSnack({ msg: "수정되었습니다.", severity: "success" });
          navigate(`${basePath}/${updated.id}`);
        } else {
          const created = await createRental(filled);
          setSnack({ msg: "등록되었습니다.", severity: "success" });
          navigate(`${basePath}/${created.id}`);
        }
      } catch (e: any) {
        setSnack({ msg: e?.message ?? "저장 실패", severity: "error" });
      } finally {
        setSubmitting(false);
      }
    },
    (errs) => {
      const fields = Object.keys(errs).join(", ");
      setSnack({
        msg: `누락 또는 잘못된 항목: ${fields}`,
        severity: "error",
      });
    }
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} sx={{ mb: 2 }}>
        목록으로
      </Button>

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {isEdit ? "세금계산서 발행 수정" : "세금계산서 발행 등록"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setPickerOpen(true)}
          >
            견적서 불러오기
          </Button>
          <Button variant="text" onClick={handleStartFromScratch}>
            초기화
          </Button>
          <Button
            variant="contained"
            type="button"
            onClick={onSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={submitting}
          >
            {isEdit ? "수정 저장" : "등록"}
          </Button>
        </Stack>
      </Box>

      {watchedEstimateId && (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`출처 견적서: ${watchedCover?.companyName || ""} / ${watchedCover?.siteName || ""}`}
          />
        </Box>
      )}

      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* ─────── Left column: all editable sections ─────── */}
            <Grid size={{ xs: 12, md: 8 }}>
              {/* Section: 거래처 정보 */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    거래처 정보
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    검색·목록에서 보이는 항목입니다. 비워 두면 아래 표지 정보에서 자동으로 채워집니다.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="clientName"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="거래처"
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="siteName"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="현장명"
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="siteAddress"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="현장 주소" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="contactPerson"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="담당자" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="contactPhone"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="연락처" fullWidth />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section: 견적서 표지 */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>견적서 사본 편집</AlertTitle>
                    여기서 수정한 내용은 이 거래의 사본에만 반영됩니다 — 원본 견적서는 변경되지 않습니다.
                  </Alert>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    표지
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.companyName"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="업체명" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.siteName"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="현장명" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.recipientName"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="수신자" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.recipientHP"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="수신자 연락처" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.companyTel"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="업체 전화번호" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="cover.estimateDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            type="date"
                            label="견적일"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section: 발행 항목 (multi-row, drives the calculation) */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    발행 항목
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    여러 모델을 자유롭게 추가할 수 있습니다. 각 행의 <strong>수량 × 단가</strong>가 월 공급가가 되며, 모든 행의 합계가 회차별 세금계산서 공급가의 기준이 됩니다. (설명·소계 행은 계산에서 제외)
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="content.contentTitle"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="제목" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="content.deliveryLocation"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="납품 장소" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="content.personInCharge"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} value={field.value ?? ""} label="담당자" fullWidth />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1 }} />

                  <ItemsEditor name="content.items" initialItems={initialItems} />
                </CardContent>
              </Card>

              {/* Section: 발행 일정 / 운반비 */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    발행 일정 · 운반비
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="transportFee"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            label="왕복 운반비 (VAT 별도, 1회차에만 합산)"
                            type="number"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(v === "" ? "" : Math.max(0, Math.floor(Number(v))));
                            }}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            inputProps={{ min: 0, step: 1000 }}
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="initialMonths"
                        control={control}
                        render={({ field, fieldState }) => (
                          <InitialMonthsField
                            value={(field.value as number) ?? ""}
                            onChange={(v) => field.onChange(v === "" ? "" : v)}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="totalRounds"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TotalRoundsField
                            value={(field.value as number) ?? ""}
                            onChange={(v) => field.onChange(v === "" ? "" : v)}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="issueDate"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="date"
                            label="세금계산서 발행일 *"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="rentalStartDate"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="date"
                            label="1회차 사용 시작일 *"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="memo"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="메모"
                            multiline
                            minRows={3}
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ─────── Right column: sticky preview ─────── */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ color: C.sub, fontWeight: 700, letterSpacing: 0 }}>
                      1회차 세금계산서 (예상)
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 1.5, fontSize: "0.85rem" }}>
                      <Row
                        label={`월 공급가 합계 (${preview.itemCount}종 / ${preview.totalContainers}대)`}
                        value={fmtKrw(preview.monthlyTotal)}
                      />
                      <Row label={`초기 ${Number(watchedInitialMonths) || 0}개월 공급가`} value={fmtKrw(preview.rentTotal)} />
                      <Row label="왕복 운반비" value={fmtKrw(Number(watchedTransportFee) || 0)} />
                      <Divider sx={{ borderColor: C.border, my: 0.5 }} />
                      <Row label="공급가액" value={fmtKrw(preview.supply)} bold />
                      <Row label="부가세 10%" value={fmtKrw(preview.vat)} />
                    </Stack>
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "baseline", borderTop: `1px solid ${C.border}`, pt: 1.5 }}>
                      <Typography sx={{ color: C.ink, fontWeight: 700 }}>합계</Typography>
                      <Typography sx={{ ...moneySx, fontWeight: 700, fontSize: "1.5rem", color: C.accent }}>
                        {fmtKrw(preview.total)}원
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <Card sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">1회차 사용기간</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {watchedRentalStartDate ? dayjs(watchedRentalStartDate).format("YYYY.MM.DD") : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ~ {preview.firstEnd || "—"}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Card sx={{ p: 1.5, bgcolor: "rgba(255, 193, 7, 0.08)", border: "1px solid", borderColor: "warning.light" }}>
                      <Typography variant="caption" color="warning.dark">2회차 발행 시작</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "warning.dark" }}>
                        {preview.regularStart || "—"}
                      </Typography>
                      <Typography variant="caption" color="warning.dark">
                        이후 매월 1회
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(33, 150, 243, 0.06)", border: "1px solid", borderColor: "info.light", borderRadius: 1 }}>
                  <Typography variant="caption" color="info.dark">
                    위 미리보기는 클라이언트 추산값입니다. 저장 시 서버가 정식으로 회차 일정을 계산하여 저장합니다.
                  </Typography>
                </Box>

                {Object.keys(formState.errors).length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    누락 또는 잘못된 항목: {Object.keys(formState.errors).join(", ")}
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate(basePath)}>
              취소
            </Button>
            <Button
              variant="contained"
              type="submit"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={submitting}
              size="large"
            >
              {isEdit ? "수정 저장" : "등록"}
            </Button>
          </Box>

          <input type="hidden" {...methods.register("status")} />
          <input type="hidden" {...methods.register("estimateId")} />
        </form>
      </FormProvider>

      <EstimatePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickEstimate}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ----------------------------------------------------------------------

interface RowProps {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}

function Row({ label, value, bold }: RowProps) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="caption" sx={{ color: C.sub }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          ...moneySx,
          fontWeight: bold ? 700 : 400,
          color: C.ink,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
